import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Layout from '../../components/Layout/Layout';
import Input from '../../components/UI/Input';
import Select from '../../components/UI/Select';
import Button from '../../components/UI/Button';
import { useLanguage } from '../../contexts/LanguageContext';
import dataService from '../../services/dataService';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import supabase from '../../lib/supabase';

const { FiSave, FiArrowLeft } = FiIcons;

const TeacherForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const isEdit = !!id;

  const [formData, setFormData] = useState({
    reg_no: '',
    full_name: '',
    school: '',
    city: '',
    governorate: '',
    email: '',
    phone: '',
    originality: 0,
    relevance: 0,
    skills: 0,
    presentation: 0,
    educational_value: 0,
    assignments_completed: true
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // List of governorates with values and labels in both languages
  const governorateOptions = [
    { value: 'amman', label: t('governorates.amman') },
    { value: 'irbid', label: t('governorates.irbid') },
    { value: 'balqa', label: t('governorates.balqa') },
    { value: 'zarqa', label: t('governorates.zarqa') },
    { value: 'mafraq', label: t('governorates.mafraq') },
    { value: 'jerash', label: t('governorates.jerash') },
    { value: 'ajloun', label: t('governorates.ajloun') },
    { value: 'madaba', label: t('governorates.madaba') },
    { value: 'karak', label: t('governorates.karak') },
    { value: 'tafilah', label: t('governorates.tafilah') },
    { value: 'maan', label: t('governorates.maan') },
    { value: 'aqaba', label: t('governorates.aqaba') }
  ];

  useEffect(() => {
    if (isEdit) {
      const teacher = dataService.getTeacherById(id);
      if (teacher) {
        setFormData({
          ...teacher,
          // Set default value for assignments_completed if it's undefined
          assignments_completed: teacher.assignments_completed !== undefined ? teacher.assignments_completed : true
        });
      } else {
        navigate('/admin');
      }
    }
  }, [id, isEdit, navigate]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Validate form
    const validationErrors = dataService.validateTeacher(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setLoading(false);
      return;
    }

    try {
      // Save to local storage via dataService
      dataService.saveTeacher(formData);

      // If we're connected to Supabase, also save there
      try {
        // Check if record exists in Supabase
        const { data: existingData } = await supabase
          .from('teachers_x7a9b3c4d2')
          .select('id')
          .eq('reg_no', formData.reg_no)
          .maybeSingle();

        if (existingData) {
          // Update existing record
          await supabase
            .from('teachers_x7a9b3c4d2')
            .update({
              full_name: formData.full_name,
              school: formData.school,
              city: formData.city,
              governorate: formData.governorate,
              email: formData.email,
              phone: formData.phone,
              originality: formData.originality,
              relevance: formData.relevance,
              skills: formData.skills,
              presentation: formData.presentation,
              educational_value: formData.educational_value,
              assignments_completed: formData.assignments_completed,
              last_updated: new Date().toISOString()
            })
            .eq('id', existingData.id);
        } else {
          // Insert new record
          await supabase.from('teachers_x7a9b3c4d2').insert({
            reg_no: formData.reg_no,
            full_name: formData.full_name,
            school: formData.school,
            city: formData.city,
            governorate: formData.governorate,
            email: formData.email,
            phone: formData.phone,
            originality: formData.originality,
            relevance: formData.relevance,
            skills: formData.skills,
            presentation: formData.presentation,
            educational_value: formData.educational_value,
            assignments_completed: formData.assignments_completed,
            last_updated: new Date().toISOString()
          });
        }
      } catch (supabaseError) {
        console.error('Supabase save error:', supabaseError);
        // Continue even if Supabase save fails - we've already saved to local storage
      }

      navigate('/admin');
    } catch (error) {
      console.error('Error saving teacher:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate computed values for display
  const total_raw = formData.originality + formData.relevance + formData.skills + formData.presentation + formData.educational_value;
  const total_pct = Math.round(total_raw * 4);
  let status_code;

  if (!formData.assignments_completed) {
    status_code = 'FAIL'; // Force FAIL status if assignments are not completed
  } else if (total_pct >= 85) {
    status_code = 'TOP_DISTINGUISHED';
  } else if (total_pct >= 50) {
    status_code = 'PASSED';
  } else {
    status_code = 'FAIL';
  }

  return (
    <Layout title={isEdit ? t('edit') + ' ' + t('teacherDetails') : t('create') + ' ' + t('teacherDetails')}>
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-lg p-6"
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3 rtl:space-x-reverse">
              <Button onClick={() => navigate('/admin')} variant="secondary" size="sm">
                <SafeIcon icon={FiArrowLeft} className="w-4 h-4" />
                <span>{t('back')}</span>
              </Button>
              <h1 className="text-2xl font-bold text-gray-900">
                {isEdit ? t('edit') + ' ' + t('teacherDetails') : t('create') + ' ' + t('teacherDetails')}
              </h1>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Identity Section */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('identity')}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label={t('registrationNumber')}
                  value={formData.reg_no}
                  onChange={(e) => handleInputChange('reg_no', e.target.value)}
                  error={errors.reg_no}
                  required
                />
                <Input
                  label={t('fullName')}
                  value={formData.full_name}
                  onChange={(e) => handleInputChange('full_name', e.target.value)}
                  error={errors.full_name}
                  required
                />
                <Input
                  label={t('school')}
                  value={formData.school}
                  onChange={(e) => handleInputChange('school', e.target.value)}
                  error={errors.school}
                />
                <Input
                  label={t('city')}
                  value={formData.city}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                  error={errors.city}
                />
                <Select
                  label={t('governorate')}
                  value={formData.governorate}
                  onChange={(e) => handleInputChange('governorate', e.target.value)}
                  options={governorateOptions}
                  placeholder={t('selectGovernorate')}
                  error={errors.governorate}
                />
                <Input
                  label={t('email')}
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  error={errors.email}
                />
                <Input
                  label={t('phone')}
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  error={errors.phone}
                />
              </div>
            </div>

            {/* Scores Section */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('scores')}</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
                <Input
                  label={t('originality')}
                  type="number"
                  min="0"
                  max="5"
                  value={formData.originality}
                  onChange={(e) => handleInputChange('originality', parseInt(e.target.value) || 0)}
                  error={errors.originality}
                  required
                />
                <Input
                  label={t('relevance')}
                  type="number"
                  min="0"
                  max="5"
                  value={formData.relevance}
                  onChange={(e) => handleInputChange('relevance', parseInt(e.target.value) || 0)}
                  error={errors.relevance}
                  required
                />
                <Input
                  label={t('skills')}
                  type="number"
                  min="0"
                  max="5"
                  value={formData.skills}
                  onChange={(e) => handleInputChange('skills', parseInt(e.target.value) || 0)}
                  error={errors.skills}
                  required
                />
                <Input
                  label={t('presentation')}
                  type="number"
                  min="0"
                  max="5"
                  value={formData.presentation}
                  onChange={(e) => handleInputChange('presentation', parseInt(e.target.value) || 0)}
                  error={errors.presentation}
                  required
                />
                <Input
                  label={t('educationalValue')}
                  type="number"
                  min="0"
                  max="5"
                  value={formData.educational_value}
                  onChange={(e) => handleInputChange('educational_value', parseInt(e.target.value) || 0)}
                  error={errors.educational_value}
                  required
                />
              </div>
            </div>

            {/* Assignments Section */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('assignments')}</h2>
              <div className="flex items-center space-x-2 rtl:space-x-reverse">
                <label className="inline-flex items-center">
                  <input 
                    type="radio" 
                    name="assignments_completed" 
                    className="form-radio h-5 w-5 text-minecraft-green"
                    checked={formData.assignments_completed === true} 
                    onChange={() => handleInputChange('assignments_completed', true)} 
                  />
                  <span className="ml-2 rtl:mr-2 rtl:ml-0">{t('yes')}</span>
                </label>
                <label className="inline-flex items-center ml-6 rtl:mr-6 rtl:ml-0">
                  <input 
                    type="radio" 
                    name="assignments_completed" 
                    className="form-radio h-5 w-5 text-minecraft-red"
                    checked={formData.assignments_completed === false} 
                    onChange={() => handleInputChange('assignments_completed', false)} 
                  />
                  <span className="ml-2 rtl:mr-2 rtl:ml-0">{t('no')}</span>
                </label>
              </div>
              {formData.assignments_completed === false && (
                <div className="mt-2 text-sm text-orange-600">
                  {t('assignmentsWarning')}
                </div>
              )}
            </div>

            {/* Computed Values */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('computed')}</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('totalRaw')}
                  </label>
                  <div className="text-2xl font-bold text-gray-900">{total_raw}/25</div>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('totalPercentage')}
                  </label>
                  <div className="text-2xl font-bold text-gray-900">{total_pct}%</div>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('statusCode')}
                  </label>
                  <div className="text-lg font-semibold">
                    <span className={`
                      px-3 py-1 rounded-full text-sm
                      ${status_code === 'TOP_DISTINGUISHED' ? 'bg-green-100 text-green-800' : ''}
                      ${status_code === 'PASSED' ? 'bg-blue-100 text-blue-800' : ''}
                      ${status_code === 'FAIL' ? 'bg-orange-100 text-orange-800' : ''}
                    `}>
                      {t(
                        status_code === 'TOP_DISTINGUISHED'
                          ? 'topDistinguished'
                          : status_code === 'PASSED'
                          ? 'passed'
                          : 'didNotPass'
                      )}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-4 rtl:space-x-reverse pt-6 border-t border-gray-200">
              <Button type="button" onClick={() => navigate('/admin')} variant="secondary">
                {t('cancel')}
              </Button>
              <Button type="submit" variant="primary" disabled={loading}>
                {loading ? (
                  <span>{t('loading')}</span>
                ) : (
                  <>
                    <SafeIcon icon={FiSave} className="w-4 h-4" />
                    <span>{t('save')}</span>
                  </>
                )}
              </Button>
            </div>
          </form>
        </motion.div>
      </div>
    </Layout>
  );
};

export default TeacherForm;